import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { type Employee, EmployeeBadge, employeesQueryOptions } from '@/entities/employee'
import type { CalendarEvent } from '@/entities/event'
import {
  calendarQueryOptions,
  eventDisplayTitle,
  eventToneClass,
  eventTypeDotClass,
} from '@/entities/event'
import { EmployeeHubNoticeDialog } from '@/features/employee-hub-notice'
import {
  addDays,
  addMonths,
  cn,
  coversDay,
  dayOfMonth,
  fmtDayShort,
  fmtMonthTitle,
  fmtTime,
  fmtWeekTitle,
  fromISODate,
  isToday,
  monthGrid,
  pluralRu,
  startOfWeek,
  toISODate,
  weekDays,
} from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { PageHeader } from '@/shared/ui/page-header'
import { Segmented } from '@/shared/ui/segmented'
import { ZoneTag } from '@/shared/ui/zone-tag'

const searchSchema = z.object({
  view: z.enum(['week', 'month']).optional().catch(undefined),
  date: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_app/team')({
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: TeamPage,
})

const MAX_CELL_CHIPS = 2

function TeamPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view = search.view ?? 'week'
  const now = new Date()
  const anchor = (search.date ? fromISODate(search.date) : null) ?? now

  const days =
    view === 'week'
      ? weekDays(anchor)
      : monthGrid(anchor)
          .filter((c) => !c.outside)
          .map((c) => c.date)
  const from = days[0] ?? anchor
  const to = addDays(days[days.length - 1] ?? anchor, 1)

  const { data: employees = [] } = useQuery(employeesQueryOptions)
  const { data: events = [] } = useQuery(calendarQueryOptions({ kind: 'team' }, from, to))

  const [hubNoticeFor, setHubNoticeFor] = useState<Employee | null>(null)

  const shift = (dir: 1 | -1) => {
    const next = view === 'week' ? addDays(startOfWeek(anchor), 7 * dir) : addMonths(anchor, dir)
    void navigate({ search: (s) => ({ ...s, date: toISODate(next) }) })
  }

  const eventsOf = (employeeId: string, day: Date): CalendarEvent[] =>
    events.filter((ev) => ev.participants.includes(employeeId) && coversDay(ev, day))

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Команда"
        description="Занятость всей команды — «свысока»: строка — сотрудник, колонка — день."
      />

      {/* Как в календарном тулбаре: на телефоне период и переключатель живут
          разными строками, с sm выстраиваются в ряд. */}
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-2 border-b px-gutter pb-3 sm:flex sm:flex-wrap sm:gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Предыдущий период"
            onClick={() => shift(-1)}
          >
            <Icon name="chevron-left" size={16} />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              void navigate({ search: (s) => ({ ...s, date: toISODate(new Date()) }) })
            }
          >
            Сегодня
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Следующий период"
            onClick={() => shift(1)}
          >
            <Icon name="chevron-right" size={16} />
          </Button>
        </div>
        <h2
          className="t-h3 min-w-0 truncate text-center first-letter:uppercase sm:flex-1 sm:basis-44 sm:text-left"
          aria-live="polite"
        >
          {view === 'week' ? fmtWeekTitle(anchor) : fmtMonthTitle(anchor)}
        </h2>
        <Segmented
          value={view}
          onChange={(v) => void navigate({ search: (s) => ({ ...s, view: v }) })}
          options={[
            { value: 'week', label: 'Неделя' },
            { value: 'month', label: 'Месяц' },
          ]}
          aria-label="Период обзора"
          className="col-span-2 w-full sm:w-auto"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto overscroll-contain pb-safe-0">
        <table className="w-full border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 bg-bg-base">
            <tr>
              {/* Левый gutter первой колонки — тот же, что у шапки страницы и
                  тулбара: сотрудники не липнут к краю контента. */}
              <th className="label sticky left-0 z-20 border-r border-b bg-bg-base py-2 pr-2 pl-gutter text-left font-medium sm:pr-3">
                <span className="flex items-center justify-between gap-2">
                  Сотрудник
                  {/* Угол матрицы — заголовок её времён: в неделе ячейки несут
                      часы, и зона названа здесь один раз на всю таблицу. В
                      месяце ячейка показывает счётчик — подписывать нечего. */}
                  {view === 'week' && <ZoneTag className="text-[10px] normal-case" />}
                </span>
              </th>
              {days.map((day) => (
                <th
                  key={day.toISOString()}
                  className={cn(
                    'border-b px-1 py-2 text-center',
                    view === 'month' && 'min-w-9',
                    view === 'week' && 'min-w-32',
                  )}
                >
                  <span
                    className={cn(
                      'label',
                      isToday(day) && 'rounded-pill bg-accent px-2 py-0.5 text-text-inverse',
                    )}
                  >
                    {view === 'week' ? fmtDayShort(day) : dayOfMonth(day)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id} className={cn(!employee.isActive && 'opacity-50')}>
                <td className="sticky left-0 z-10 border-r border-b bg-bg-base py-1.5 pr-2 pl-gutter sm:pr-3">
                  {/* Ширина задаётся здесь, а не на ячейке: в table-layout auto
                      td растёт под контент, и длинное имя выдавило бы колонку
                      сотрудника на пол-экрана вместо усечения. */}
                  <div className="flex w-36 items-center justify-between gap-1 sm:w-56">
                    <Link
                      to="/employees/$employeeId"
                      params={{ employeeId: employee.id }}
                      className="min-w-0 flex-1 transition-opacity hover:opacity-80"
                    >
                      <EmployeeBadge employee={employee} size="sm" className="w-full" />
                    </Link>
                    <Menu
                      aria-label={`Действия: ${employee.displayName}`}
                      trigger={<Icon name="dots" size={14} />}
                      items={[
                        {
                          label: 'Управление — в хабе',
                          icon: 'external-link',
                          onSelect: () => setHubNoticeFor(employee),
                        },
                      ]}
                    />
                  </div>
                </td>
                {days.map((day) => {
                  const cellEvents = eventsOf(employee.id, day)
                  const overflow = cellEvents.length - MAX_CELL_CHIPS
                  return (
                    <td
                      key={day.toISOString()}
                      className={cn(
                        'border-b align-top',
                        isToday(day) && 'bg-bg-elevated',
                        view === 'week' ? 'border-l p-0.5' : 'p-0 text-center',
                      )}
                    >
                      {view === 'week' ? (
                        <div className="flex flex-col gap-0.5">
                          {cellEvents.slice(0, MAX_CELL_CHIPS).map((ev) => (
                            <Link
                              key={ev.id}
                              to="/employees/$employeeId"
                              params={{ employeeId: employee.id }}
                              search={{ view: 'day', date: toISODate(day) }}
                              className={cn(
                                'flex min-w-0 items-center gap-1 rounded-[5px] px-1 py-0.5 text-[11px] transition-colors hover:bg-bg-raised',
                                eventToneClass(ev, now),
                              )}
                              aria-label={`${eventDisplayTitle(ev)}, открыть день сотрудника`}
                            >
                              <span
                                className={cn(
                                  'size-1.5 shrink-0 rounded-pill',
                                  eventTypeDotClass[ev.type],
                                )}
                              />
                              {!ev.allDay && (
                                <span className="mono-num shrink-0 text-[10px] text-text-tertiary">
                                  {fmtTime(ev.startAt)}
                                </span>
                              )}
                              <span className="truncate text-text-secondary">
                                {eventDisplayTitle(ev)}
                              </span>
                            </Link>
                          ))}
                          {overflow > 0 && (
                            <Link
                              to="/employees/$employeeId"
                              params={{ employeeId: employee.id }}
                              search={{ view: 'day', date: toISODate(day) }}
                              className="px-1 text-[10px] text-text-tertiary hover:text-text-secondary"
                            >
                              ещё {overflow}
                            </Link>
                          )}
                        </div>
                      ) : (
                        cellEvents.length > 0 && (
                          <Link
                            to="/employees/$employeeId"
                            params={{ employeeId: employee.id }}
                            search={{ view: 'day', date: toISODate(day) }}
                            aria-label={`${cellEvents.length} ${pluralRu(cellEvents.length, 'событие', 'события', 'событий')}, ${fmtDayShort(day)}`}
                            className={cn(
                              'mono-num inline-flex size-6 items-center justify-center rounded-[6px] text-[10px] transition-colors hover:bg-bg-raised',
                              cellEvents.length >= 4
                                ? 'text-accent'
                                : cellEvents.length >= 2
                                  ? 'text-text-primary'
                                  : 'text-text-tertiary',
                            )}
                          >
                            {cellEvents.length}
                          </Link>
                        )
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EmployeeHubNoticeDialog employee={hubNoticeFor} onClose={() => setHubNoticeFor(null)} />
    </div>
  )
}
